import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Grid,
  Typography,
  Button,
  Paper,
  Divider,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Alert,
  Card,
  CardContent,
  CardActions,
  IconButton,
} from "@mui/material";

import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import { Navigate, useNavigate } from "react-router-dom";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";

import { botaoVoltar } from "../../../estilos/botoes";
import { api } from "@api/api";
import { ajustarTamanhoFonteEscola } from "../../../estilos/global";
import TransicaoPagina from "../../../componentes/comuns/TransicaoPagina";

export default function Documentos({ onReturn, onRegister }) {
  const [documents, setDocuments] = useState([]);
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [processType, setProcessType] = useState("");
  const [fileInputs, setFileInputs] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const storedData = sessionStorage.getItem("processData");

    if (storedData) {
      const parsedData = JSON.parse(storedData);
      setProcessType(parsedData.processType);
      fetchDocuments(parsedData.processType);
    }
  }, []);

  const fetchDocuments = async (processId) => {
    try {
      const response = await axios.get(api.fetchDocuments(processId), {
        withCredentials: true,
      });

      setDocuments(response.data);
    } catch (error) {
      console.error("Erro ao buscar documentos:", error);
      setSnackbarMessage("Erro ao buscar documentos");
      setSnackbarOpen(true);
    }
  };

  const handleUpload = (id, event) => {
    const file = event.target.files[0];

    if (file) {
      if (file.type !== "application/pdf") {
        setSnackbarMessage("Apenas arquivos PDF são permitidos!");
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
        return;
      }

      setUploadedDocs((prev) => ({
        ...prev,
        [id]: {
          uploaded: true,
          name: file.name,
          size: file.size,
        },
      }));

      setFileInputs((prev) => ({
        ...prev,
        [id]: file,
      }));
    }
  };

  const handleRemoveFile = (id) => {
    setUploadedDocs((prev) => {
      const newUploadedDocs = { ...prev };
      delete newUploadedDocs[id];
      return newUploadedDocs;
    });

    setFileInputs((prev) => {
      const newFileInputs = { ...prev };
      delete newFileInputs[id];
      return newFileInputs;
    });
  };

  const uploadFiles = async (id_processo) => {
    const formData = new FormData();

    formData.append("id_processo", id_processo);

    Object.keys(fileInputs).forEach((docId) => {
      formData.append("files", fileInputs[docId]);
      formData.append("documento_id[]", docId);
    });

    try {
      const uploadResponse = await axios.post(api.uploadDocs(), formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        withCredentials: true,
      });

      return {
        success: true,
        message: "Documentos enviados com sucesso!",
      };
    } catch (error) {
      console.error("Erro ao enviar documentos:", error);

      return {
        success: false,
        message: error.response?.data?.message || "Erro ao enviar documentos.",
      };
    }
  };

  const allDocumentsUploaded = documents.every(
    (doc) => uploadedDocs[doc.id]?.uploaded,
  );

  const handleRegister = async () => {
    if (isSubmitting) return;

    if (!allDocumentsUploaded || !isConfirmed) {
      setSnackbarMessage(
        "Todos os documentos devem ser anexados e confirmados.",
      );
      setSnackbarOpen(true);
      return;
    }

    const storedData = sessionStorage.getItem("processData");

    if (!storedData) {
      setSnackbarMessage("Erro: Dados do processo não encontrados.");
      setSnackbarOpen(true);
      return;
    }

    const parsedData = JSON.parse(storedData);

    const dadosProcesso = {
      processType: parsedData.processType,
      description: "",
      criadoEm: new Date().toISOString(),
    };

    if (parsedData.selectedSchool) {
      dadosProcesso.selectedSchool = parsedData.selectedSchool;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(api.createProcess(), dadosProcesso, {
        withCredentials: true,
      });

      if (!response.data.processo?.id) {
        throw new Error("Erro ao obter ID do processo.");
      }

      const id_processo = response.data.processo.id;
      const uploadResult = await uploadFiles(id_processo);

      if (!uploadResult.success) {
        throw new Error(uploadResult.message);
      }

      setSnackbarMessage("Processo e documentos registrados com sucesso!");
      setSnackbarOpen(true);

      await new Promise((resolve) => setTimeout(resolve, 2000));

      navigate("/novoProcesso");
    } catch (error) {
      console.error("Erro ao registrar processo:", error);

      setSnackbarMessage(
        error.response?.data?.message || "Erro ao registrar o processo.",
      );
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleReturn = () => {
    sessionStorage.removeItem("processData");
    navigate("/novoProcesso");
  };

  return (
    <TransicaoPagina variante="navegacao">
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackRoundedIcon />}
            onClick={handleReturn}
            sx={[botaoVoltar, { mb: 2 }]}
          >
            Voltar ao novo processo
          </Button>

          <Typography
            variant="h5"
            sx={{ fontWeight: "bold", color: "#333", mb: 1 }}
          >
            Documentos necessários para o processo:
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={3} sx={{ padding: "16px", borderRadius: "8px" }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    backgroundColor: "#fff3e0",
                    border: "1px solid #ffb74d",
                    borderRadius: "6px",
                    padding: "8px 12px",
                  }}
                >
                  <WarningAmberIcon sx={{ color: "#f57c00" }} />

                  <Typography
                    variant="body2"
                    sx={{ color: "#f57c00", fontWeight: "500" }}
                  >
                    Atenção: Anexar apenas arquivos PDF.
                  </Typography>
                </Paper>
              </Grid>

              {documents.map((doc) => (
                <Grid item xs={12} key={doc.id}>
                  <Card
                    sx={{
                      border: uploadedDocs[doc.id]?.uploaded
                        ? "1px solid #4caf50"
                        : "1px solid #e0e0e0",
                      borderRadius: "6px",
                      transition: "border-color 0.3s ease",
                      "&:hover": {
                        borderColor: uploadedDocs[doc.id]?.uploaded
                          ? "#388e3c"
                          : "#1976d2",
                      },
                    }}
                  >
                    <CardContent sx={{ padding: "12px" }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "500", mb: 0.5 }}
                      >
                        {doc.nome}
                      </Typography>

                      {doc.descricao && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#555",
                            mb: 1,
                            lineHeight: 1.5,
                            whiteSpace: "pre-line",
                          }}
                        >
                          {doc.descricao}
                        </Typography>
                      )}

                      {uploadedDocs[doc.id]?.uploaded ? (
                        <div>
                          <Typography
                            variant="body2"
                            sx={{ color: "#4caf50", mb: 0.5 }}
                          >
                            Arquivo enviado: {uploadedDocs[doc.id].name} (
                            {uploadedDocs[doc.id].size} bytes)
                          </Typography>
                        </div>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
                            display: "inline-block",
                            color: "#e65100",
                            backgroundColor: "#fff3e0",
                            border: "1px solid #ffcc80",
                            borderRadius: "4px",
                            px: 1,
                            py: 0.5,
                            mt: 0.5,
                            mb: 0.5,
                            fontWeight: "500",
                          }}
                        >
                          Nenhum arquivo enviado ainda.
                        </Typography>
                      )}
                    </CardContent>

                    <CardActions
                      sx={{
                        justifyContent: "flex-end",
                        padding: "8px",
                      }}
                    >
                      {uploadedDocs[doc.id]?.uploaded ? (
                        <IconButton
                          onClick={() => handleRemoveFile(doc.id)}
                          sx={{ color: "#f44336", padding: "6px" }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <Button
                          variant="contained"
                          component="label"
                          startIcon={<AttachFileIcon fontSize="small" />}
                          sx={{
                            minHeight: 42,
                            px: 2,
                            backgroundColor: "#1976d2",
                            color: "#ffffff",
                            fontSize: ajustarTamanhoFonteEscola("0.875rem"),
                            fontWeight: 500,
                            textTransform: "none",
                            boxShadow: "none",
                            "&:hover": {
                              backgroundColor: "#1565c0",
                              boxShadow: "none",
                            },
                          }}
                        >
                          Anexar
                          <input
                            id={`file-input-${doc.id}`}
                            type="file"
                            hidden
                            onChange={(event) => handleUpload(doc.id, event)}
                            accept="application/pdf"
                          />
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={1} alignItems="center" sx={{ mt: 1 }}>
              <Grid item xs>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isConfirmed}
                      onChange={() => setIsConfirmed((prev) => !prev)}
                      sx={{ color: "#1976d2", padding: "6px" }}
                    />
                  }
                  label="Confirmo que as informações fornecidas estão corretas."
                  sx={{
                    fontSize: ajustarTamanhoFonteEscola("0.875rem"),
                  }}
                />
              </Grid>

              <Grid item>
                <Button
                  variant="contained"
                  onClick={handleRegister}
                  disabled={
                    !allDocumentsUploaded || !isConfirmed || isSubmitting
                  }
                  sx={{
                    minHeight: 42,
                    px: 2,
                    backgroundColor: "#4caf50",
                    color: "#ffffff",
                    fontSize: ajustarTamanhoFonteEscola("0.875rem"),
                    fontWeight: 500,
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#388e3c",
                      boxShadow: "none",
                    },
                  }}
                >
                  {isSubmitting ? "Registrando..." : "Registrar Processo"}
                </Button>
              </Grid>

              <Grid item>
                <Button
                  variant="outlined"
                  onClick={handleReturn}
                  sx={botaoVoltar}
                >
                  Voltar
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity="info"
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Grid>
    </TransicaoPagina>
  );
}
